import type {
  AgentBillingPlan,
  FeatureType,
  RateCard,
} from "schemas/agent-billing";

import React from "react";

import Coin from "@svg/billing/Coin";
import ProIconGlobe from "@svg/billing/Globe";
import MostPopular from "@svg/billing/MostPopular";
import PaperAirplane from "@svg/billing/PaperAirplane";
import Target from "@svg/billing/Target";

import { formatNumber } from "replo-utils/lib/format";

type ProductSection =
  | "general"
  | "replo-agent"
  | "shopify-theme-editor"
  | "analytics"
  | "ab-testing";

export type SectionFeatures = {
  included: React.ReactNode[];
  notIncluded: React.ReactNode[];
};

type UpsellCta = {
  title: string;
  description: string;
  icon: React.ReactNode;
  link: string;
};

type ProductDisplayConfig = {
  badge?: React.ReactNode;
  modalGraphicSrc: string;
  icon: React.ReactNode;
  getFeatureCopy: (
    rateCard: RateCard,
  ) => Record<ProductSection, SectionFeatures>;
  upsellCta?: UpsellCta;
};

export const FREE_DISPLAY_CONFIG = {
  icon: <PaperAirplane />,
  title: "Free",
} as const;

const getRateCardFeatureDisplay = (
  rateCard: RateCard,
  featureType: FeatureType,
): string => {
  const included = rateCard.features.find(
    (feature) => feature.featureType === featureType,
  );
  if (!included || included.units === null) {
    return "Unlimited";
  }
  return formatNumber(included.units);
};

export const PRODUCT_DISPLAY_CONFIG: Record<
  AgentBillingPlan,
  ProductDisplayConfig
> = {
  starter: {
    badge: <MostPopular />,
    modalGraphicSrc: "/images/billing/starter-modal-graphic.png",
    icon: <Coin />,
    getFeatureCopy: (rateCard) => {
      const chatLimit = getRateCardFeatureDisplay(rateCard, "credits");
      const publishedLimit = getRateCardFeatureDisplay(
        rateCard,
        "shopifyThemeBuilder.publishedElements",
      );

      return {
        general: {
          included: ["Full template library access", "Unlimited team members"],
          notIncluded: [
            "Dedicated Slack channel",
            "White-glove services",
            "Dedicated account manager",
            "1:1 onboarding & training",
          ],
        },
        "replo-agent": {
          included: [
            <>
              <span className="font-bold">{chatLimit} credits</span>
              /month
            </>,
            <>
              <span className="font-bold">
                Build, update, and optimize your commerce site
              </span>{" "}
              with Replo
            </>,
            <>
              <span className="font-bold">Connect integrations</span> across
              your commerce stack
            </>,
            <>
              <span className="font-bold">Use skills</span> for repeatable
              commerce workflows
            </>,
            <>
              <span className="font-bold">Custom domains</span> included
            </>,
            <>
              <span className="font-bold">100K visitor sessions</span> included,
              then <span className="font-bold">$1.50</span>/1k
            </>,
            "No Replo branding on generated sites",
            "SEO optimization for your site",
          ],
          notIncluded: [
            "Volume session discounts",
            "Turn ad creative into site content",
            "Lower payment processing fees",
          ],
        },
        "shopify-theme-editor": {
          included: [
            <>
              <span className="font-bold">
                {publishedLimit} published items
              </span>{" "}
              on Shopify
            </>,
            "Unlimited pageviews",
            <>
              <span className="font-bold">5K</span> A/B test sessions monthly
            </>,
            "Drag-and-drop Shopify Theme Builder",
            "Conversion-tested template library",
            "Direct integration with Shopify products & collections",
            "Built-in assistant for faster page building",
          ],
          notIncluded: [],
        },
        analytics: {
          included: [
            "Full analytics across all your published pages",
            "Track pageviews, sessions, conversion rate, AOV & revenue",
            "Flexible attribution models including fractional",
            "Real-time trends and KPI dashboards",
          ],
          notIncluded: ["Optimization recommendations"],
        },
        "ab-testing": {
          included: [
            "Forever links for split-tests",
            "Real-time conversion tracking",
            "Unlimited test variants",
            "Test any page, Replo or external",
          ],
          notIncluded: [],
        },
      };
    },
  },
  pro: {
    modalGraphicSrc: "/images/billing/pro-modal-graphic.png",
    icon: <ProIconGlobe />,
    upsellCta: {
      title: "Looking for more?",
      description: "Chat with our team to get dedicated support.",
      icon: <Target width={40} height={34} />,
      link: "https://www.replo.app/contact?utm_source=dashboard",
    },
    getFeatureCopy: (rateCard) => ({
      general: {
        included: [
          "Priority chat support",
          "Full template library access",
          "Unlimited team members",
          "Dedicated Slack channel",
          "White-glove services",
          "Dedicated account manager",
          "1:1 onboarding & training",
        ],
        notIncluded: [],
      },
      "replo-agent": {
        included: [
          <>
            <span className="font-bold">
              {getRateCardFeatureDisplay(rateCard, "credits")} credits
            </span>
            /month
          </>,
          <>
            <span className="font-bold">
              Build, update, and optimize your commerce site
            </span>{" "}
            with Replo
          </>,
          <>
            <span className="font-bold">Connect integrations</span> across your
            commerce stack
          </>,
          <>
            <span className="font-bold">Use skills</span> for repeatable
            commerce workflows
          </>,
          <>
            <span className="font-bold">Custom domains</span>
          </>,
          <>
            <span className="font-bold">500,000 visitor sessions</span>{" "}
            included, then <span className="font-bold">$1.00</span>/1k
          </>,
          "No Replo branding on generated sites",
          "SEO optimization for your site",
          <>
            <span className="font-bold">33% off</span> volume session pricing
          </>,
          <>
            <span className="font-bold">Turn ad creative</span> into site
            content
          </>,
          "Lower payment processing fees",
        ],
        notIncluded: [],
      },
      "shopify-theme-editor": {
        included: [
          <>
            <span className="font-bold">Unlimited published items</span> on
            Shopify
          </>,
          "Unlimited pageviews",
          <>
            <span className="font-bold">100,000</span> A/B test sessions monthly
          </>,
          "Visual drag-and-drop Shopify Theme Builder",
          "Conversion-tested template library",
          "Auto-sync Shopify products & collections",
          "Built-in assistant for faster page building",
        ],
        notIncluded: [],
      },
      analytics: {
        included: [
          "Full analytics across all your published pages",
          "Track pageviews, sessions, conversion rate, AOV & revenue",
          "Flexible attribution models including fractional",
          "Real-time trends and KPI dashboards",
          "Optimization recommendations",
        ],
        notIncluded: [],
      },
      "ab-testing": {
        included: [
          "Forever links for split-tests",
          "Real-time conversion tracking",
          "Unlimited test variants",
          "Test any page, Replo or external",
        ],
        notIncluded: [],
      },
    }),
  },
};
